export type SubscriptionStatus = 'active' | 'inactive' | 'past_due' | 'canceled';

export type DashboardSection =
  | 'home'
  | 'quickstart'
  | 'analytics'
  | 'audio-bridge'
  | 'integrations'
  | 'ptt'
  | 'ai-voice'
  | 'chat-commands'
  | 'profanity-filter'
  | 'viewer-memory'
  | 'activity'
  | 'settings';

export function parseSubscriptionStatus(status: string | undefined): SubscriptionStatus | null {
  return status === 'active' || status === 'inactive' || status === 'past_due' || status === 'canceled' ? status : null;
}

export function normalizeSubscriptionStatus(status: string | undefined): SubscriptionStatus {
  return parseSubscriptionStatus(status) ?? 'inactive';
}

export function getQuickStartDefaultSection(isLive: boolean): DashboardSection {
  return isLive ? 'activity' : 'home';
}

type DashboardReadinessTone = 'ready' | 'live' | 'warning' | 'blocked';

interface DashboardReadinessInput {
  isLive: boolean;
  subscriptionExpired: boolean;
  isOverlayConnected: boolean;
  assistantName: string;
  pttEnabled: boolean;
  pttHotkeyDisplay: string;
}

export function getDashboardReadiness({
  isLive,
  subscriptionExpired,
  isOverlayConnected,
  assistantName,
  pttEnabled,
  pttHotkeyDisplay,
}: DashboardReadinessInput): { title: string; description: string; tone: DashboardReadinessTone } {
  if (subscriptionExpired && !isLive) {
    return {
      title: 'Plan attention needed',
      description: 'Upgrade to stream with StreamBuddy.',
      tone: 'blocked',
    };
  }

  if (!isOverlayConnected) {
    if (isLive) {
      return {
        title: 'Overlay disconnected',
        description: `Open OBS or fix the Browser Source URL so ${assistantName} audio reaches stream.`,
        tone: 'warning',
      };
    }

    return {
      title: 'Connect OBS overlay',
      description: 'Open OBS or update the Browser Source URL before going live.',
      tone: 'warning',
    };
  }

  if (isLive) {
    return {
      title: 'Live now',
      description: `${assistantName} is active. ${pttEnabled ? `Hold ${pttHotkeyDisplay} to talk.` : 'Hands-free voice is enabled.'}`,
      tone: 'live',
    };
  }

  return {
    title: 'Ready to go live',
    description: 'Everything looks good. You are all set.',
    tone: 'ready',
  };
}
